/**
 * Base Service Class
 * 
 * Provides common functionality for all services following Module M8 (Output Contract)
 * from the ConnectPath Modular Methodology.
 */

import { config } from '../../../config/environment';
import { ServiceError, ServiceResponse } from '../../interfaces/IDataSource';
import { api } from '../../api';

export abstract class BaseService {
  protected serviceName: string;
  protected apiUrl: string;
  protected cache: Map<string, { data: any; timestamp: number }> = new Map();

  constructor(serviceNameOrApiUrl: string) {
    // Support both old and new constructor signatures
    if (serviceNameOrApiUrl.startsWith('http')) {
      this.apiUrl = serviceNameOrApiUrl;
      this.serviceName = 'BaseService';
    } else {
      this.serviceName = serviceNameOrApiUrl;
      this.apiUrl = config.apiUrl;
    }
  }

  /**
   * Get data from cache if available and not expired
   */
  protected getFromCache<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > config.cacheTimeout) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  /**
   * Store data in cache
   */
  protected setCache(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Clear specific cache entry or all cache
   */
  protected clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Create a service response
   */
  protected createResponse<T>(data: T, success: boolean = true, error?: string): ServiceResponse<T> {
    return {
      data,
      success,
      error,
      metadata: {
        timestamp: new Date(),
        source: this.constructor.name
      }
    };
  }

  /**
   * Create a service error
   */
  protected createError(code: string, message: string, details?: any): ServiceError {
    return {
      code,
      message,
      details,
      timestamp: new Date()
    };
  }

  /**
   * Handle API errors consistently
   */
  protected handleError(error: any): never {
    if (config.enableDebugLogging) {
      console.error(`[${this.constructor.name}] Error:`, error);
    }

    if (error.response) {
      // Server responded with error
      throw this.createError(
        error.response.data?.code || 'server_error',
        error.response.data?.message || 'Server error occurred',
        error.response.data
      );
    } else if (error.request) {
      // Request made but no response
      throw this.createError(
        'network_error',
        'Network error occurred',
        { originalError: error.message }
      );
    } else {
      // Something else happened
      throw this.createError(
        'unknown_error',
        error.message || 'An unknown error occurred',
        error
      );
    }
  }

  /**
   * Log debug information if enabled
   */
  protected debug(message: string, data?: any): void {
    if (config.enableDebugLogging) {
      console.log(`[${this.constructor.name}] ${message}`, data || '');
    }
  }

  /**
   * Simulate API delay for mock services
   */
  protected async simulateDelay(min: number = 100, max: number = 500): Promise<void> {
    const delay = Math.random() * (max - min) + min;
    return new Promise(resolve => setTimeout(resolve, delay));
  }

  /**
   * Generate a unique ID for mock data
   */
  protected generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Simulate network delay for mock services
   */
  protected async simulateNetworkDelay(min: number = 100, max: number = 500): Promise<void> {
    return this.simulateDelay(min, max);
  }

  /**
   * Execute a function with caching
   */
  protected async withCache<T>(
    key: string,
    fn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    // Check cache first
    const cached = this.getFromCache<T>(key);
    if (cached !== null) {
      this.debug(`Cache hit for key: ${key}`);
      return cached;
    }

    // Execute function and cache result
    this.debug(`Cache miss for key: ${key}`);
    const result = await fn();
    this.setCache(key, result);
    return result;
  }

  /**
   * Deep clone an object to prevent mutations
   */
  protected deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * HTTP GET method
   */
  protected async get<T>(url: string, params?: any): Promise<T> {
    const response = await api.get<T>(url, { params });
    return response.data;
  }

  /**
   * HTTP POST method
   */
  protected async post<T>(url: string, data?: any): Promise<T> {
    const response = await api.post<T>(url, data);
    return response.data;
  }

  /**
   * HTTP PUT method
   */
  protected async put<T>(url: string, data?: any): Promise<T> {
    const response = await api.put<T>(url, data);
    return response.data;
  }

  /**
   * HTTP DELETE method
   */
  protected async delete<T>(url: string): Promise<T> {
    const response = await api.delete<T>(url);
    return response.data;
  }

  /**
   * HTTP PATCH method
   */
  protected async patch<T>(url: string, data?: any): Promise<T> {
    const response = await api.patch<T>(url, data);
    return response.data;
  }
}